import axios from 'axios';
import {
  savePendingProfileEdit,
  clearPendingProfileEdit,
} from '../utils/pendingProfileEdit';
import { toast } from 'react-toastify';
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  USER_LOADED,
  AUTH_ERROR,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  CLEAR_PROFILE,
  UPDATE_FAIL,
  GET_PROFILE,
  PROFILE_ERROR,
  INCREMENT_SOCIAL_CLICK,
  SESSION_EXPIRED,
} from './types';
// import { setAlert } from "./alerts";
import setAuthToken from '../utils/setAuthToken';
import { unsubscribeFromPush } from '../utils/usePushNotifications';

// Load User
export const loadUser = () => async (disptach) => {
  if (localStorage.token) setAuthToken(localStorage.token);
  try {
    const res = await axios.get('/api/auth/current');
    disptach({
      type: USER_LOADED,
      payload: res.data,
    });
  } catch (err) {
    // A session that ended is not an authentication failure, and the
    // difference matters here more than anywhere: AUTH_ERROR clears
    // isAuth, and every editing screen redirects on !isAuth, so routing
    // an expired session through it would unmount whatever the customer
    // was in the middle of -- which is the exact thing the rest of this
    // change exists to prevent.
    //
    // SESSION_EXPIRED leaves isAuth alone and only resolves `loading`.
    // On a cold page load that is still correct: isAuth was never true in
    // the first place, so the app renders and sends them to sign in. Mid
    // session it is what keeps the form on screen.
    const status = err.response && err.response.status;
    const code = err.response && err.response.data && err.response.data.code;
    if (status === 401 && (code === 'session_expired' || code === 'password_changed')) {
      disptach({
        type: SESSION_EXPIRED,
        payload: { msg: err.response.data.msg },
      });
      return;
    }
    disptach({
      type: AUTH_ERROR,
    });
  }
};
// Register User
export const registerUser = ({ name, email, password }) => async (disptach) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };
  const body = JSON.stringify({ name, email, password });

  try {
    const res = await axios.post('/api/users/register', body, config);
    disptach({
      type: REGISTER_SUCCESS,
      payload: res.data,
    });
    disptach(loadUser());
  } catch (err) {
    // alert('Email Already Registered');
    alert('🚫Email Already Registered');
    disptach({
      type: REGISTER_FAIL,
    });
  }
};

// Login User
export const loginUser = (email, password) => async (disptach) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };
  const body = JSON.stringify({ email, password });

  try {
    const res = await axios.post('/api/auth/login', body, config);

    // TOTP-enabled account: the server issues a pending token, not the
    // real one, and there is nothing to dispatch to redux yet -- this
    // isn't a completed login. Returned to the caller (Login.js) so it
    // can hold the pending token as local UI state and show the code
    // screen; verifyTotp below completes the login once a code is
    // confirmed.
    if (res.data.pending) {
      return { pending: true, pendingToken: res.data.pendingToken };
    }

    disptach({
      type: LOGIN_SUCCESS,
      payload: res.data,
    });

    disptach(loadUser());
    return { pending: false };
  } catch (err) {
    // const errors = err.response.data.errors;

    // if (errors) {
    //   errors.forEach((error) => disptach(setAlert(error.msg, "danger")));
    // }
    // alert('Username OR Password is not correct');
    alert('🚫 Email OR Password is not correct');
    disptach({
      type: LOGIN_FAIL,
    });
    return { pending: false };
  }
};

// Second step of login for a TOTP-enabled account: exchanges the pending
// token + a 6-digit code for the real JWT. On success this completes the
// login exactly the same way loginUser above does for a non-2FA account.
export const verifyTotp = (pendingToken, code) => async (disptach) => {
  try {
    const res = await axios.post('/api/auth/totp/verify', { pendingToken, code });
    disptach({
      type: LOGIN_SUCCESS,
      payload: res.data,
    });
    disptach(loadUser());
    return { success: true };
  } catch (err) {
    const msg = (err.response && err.response.data && err.response.data.msg) || 'Incorrect code';
    alert(`🚫 ${msg}`);
    return { success: false };
  }
};

// Consumes a claim link's token + a customer-chosen password (see
// ClaimCard.js): completes the same way loginUser/verifyTotp above do,
// landing the customer logged in immediately rather than sent to a
// separate login screen. Errors are returned rather than alert()'d
// (unlike verifyTotp) so ClaimCard.js can show them inline next to the
// password form instead of as a jarring popup.
export const claimCard = (claimToken, password) => async (disptach) => {
  try {
    const res = await axios.post(`/api/users/claim/${claimToken}`, { password });
    disptach({
      type: LOGIN_SUCCESS,
      payload: res.data,
    });
    disptach(loadUser());
    return { success: true };
  } catch (err) {
    const msg = (err.response && err.response.data && err.response.data.msg) || 'Something went wrong. Please try again.';
    return { success: false, msg };
  }
};

// // Add Picture
// export const addPic = (obj) => async (disptach) => {
//   const config = {
//     headers: {
//       'Content-type': 'application/json',
//     },
//   };
//   const body = JSON.stringify(obj);
//   try {
//     await axios.post('/api/users/add_pic', body, config);
//     disptach(loadUser());
//   } catch (err) {
//     disptach({
//       type: UPDATE_FAIL,
//     });
//   }
// };
export const updateUser = (obj, history) => async (disptach) => {
  const config = {
    headers: {
      'Content-type': 'application/json',
    },
  };

  const body = JSON.stringify(obj);

  try {
    await axios.post('/api/users/update_user', body, config);

    // Saved -- there is nothing outstanding to hold on to any more.
    clearPendingProfileEdit();

    disptach(loadUser());

    if (
      obj.bio !== undefined &&
      obj.avatarUrl !== undefined &&
      obj.social !== undefined
    )
      history.push(`/${obj.lengthId}`);
  } catch (err) {
    console.log(err);

    // The session ended mid-save. The interceptor has already dispatched
    // SESSION_EXPIRED (which deliberately does not unmount this screen),
    // so the form is still on the page exactly as the customer left it.
    // Stashing it as well means even a reload or a trip to the sign-in
    // page cannot lose it. Returning early avoids a generic "something
    // went wrong", which would be both untrue and unhelpful -- retrying
    // cannot work until they sign in again.
    const status = err.response && err.response.status;
    const code = err.response && err.response.data && err.response.data.code;
    if (status === 401 && (code === 'session_expired' || code === 'password_changed')) {
      savePendingProfileEdit(obj);
      return;
    }

    // A blocking alert() can freeze the page in some browser/extension
    // combinations (form submission errors here are also the failure
    // mode of the stray-outer-form-submit bug fixed in
    // EmailChangeControl.js/UsernameChangeControl.js) -- toast matches
    // the pattern already used everywhere else in the app (see
    // AdminEmailField.js, EmailChangeControl.js, etc.) and never blocks
    // the page.
    const message =
      (err.response && err.response.data && err.response.data.errors) ||
      'Something went wrong. Please try again.';
    toast.error(`${message}`);
    // disptach({
    //   type: UPDATE_FAIL,
    // });
  }
};

export const getCurrentProfile = () => async (dispatch) => {
  try {
    const res = await axios.get('/api/profile/current');
    dispatch({
      type: GET_PROFILE,
      payload: res.data,
    });
  } catch (err) {
    dispatch({
      type: PROFILE_ERROR,
      payload: { msg: err.response.statusText, status: err.response.status },
    });
  }
};

// Records a click on the owner's own dashboard. The server no longer
// returns click counts (that response is shared with the public,
// unauthenticated surface), so the click count shown here is bumped
// locally rather than read back from the request -- the request itself
// only ever needs to say *which* social was clicked, never a count.
export const recordSocialClick = (entry, id) => async (dispatch) => {
  const name = entry && entry.platform;

  dispatch({
    type: INCREMENT_SOCIAL_CLICK,
    payload: { name },
  });

  try {
    // entryId identifies the exact socialEntries row and is what the
    // server prefers; name is still sent because an entry derived from
    // the legacy `social` object (for a user the migration hasn't
    // reached) has no _id to send, and the server falls back to
    // platform-matching in that case.
    await axios.post(
      `/api/users/update_clicks/${id}`,
      JSON.stringify({ entryId: entry && entry._id, name }),
      { headers: { 'Content-type': 'application/json' } }
    );
  } catch (err) {
    // Best-effort -- the optimistic UI update above already happened;
    // a failed request here just means this one click isn't recorded
    // server-side, not worth surfacing to the user.
  }
};

// Marks the logged-in user's leads as viewed (called when /leads mounts).
// Re-runs loadUser() afterward so the fresh unreadLeadCount (now 0)
// reaches the badge without a page reload -- this is the one place a
// second /api/auth/current request is actually needed, since the badge
// count itself just rides along on whichever load already happened.
export const markLeadsViewed = () => async (dispatch) => {
  try {
    await axios.patch('/api/users/leads/viewed');
    dispatch(loadUser());
  } catch (err) {
    // Best-effort -- worst case the badge stays visible until the next
    // successful call, not worth surfacing to the user.
  }
};

// Records that the user dismissed the payment-methods beta notice.
//
// Deliberately does NOT dispatch loadUser() afterwards, unlike
// markLeadsViewed above: this fires in the middle of editing a profile,
// and refetching the user mid-edit is a needless chance to disturb the
// editor's state for a flag nothing on the current screen re-reads. The
// modal is already closed locally; the server value only has to be right
// by the next page load.
//
// Best-effort: if it fails the worst case is the notice appearing once
// more, which is not worth interrupting someone mid-edit to report.
export const acknowledgePaymentNotice = () => async () => {
  try {
    await axios.patch('/api/users/payment-notice/acknowledged');
  } catch (err) {
    // Intentionally silent -- see above.
  }
};

// Logout user

export const logout = () => (dispatch) => {
  // Best-effort, not awaited -- a push-unsubscribe failure (or a slow
  // service worker/network) must never delay or block logout itself. The
  // auth token is still attached to axios's default headers at this point
  // (nothing here clears it), so the server call still has what it needs.
  unsubscribeFromPush().catch((err) => {
    console.error('Push unsubscribe on logout failed:', err.message);
  });

  dispatch({
    type: CLEAR_PROFILE,
  });
  dispatch({
    type: LOGOUT,
  });
};
